import React from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Table, Divider, Button, Tooltip, Modal, Select, Input, Row, Col, Checkbox, DatePicker, Card, List, Grid, Typography } from 'antd'
import { EyeOutlined, FileTextOutlined, PlusSquareTwoTone, CloseOutlined, FilterOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { isMobile as isMobileDevice } from 'react-device-detect'
import dayjs from 'dayjs'
import _ from 'lodash'

import SelectOrderStateContainer from '../../containers/SelectOrderState'
import SelectSupplier from '../../containers/SelectSupplier'
import PurchaseOrderFormContainer from '../../containers/PurchaseOrderForm'
import PurchaseOrderStatusSwitches from '../../containers/PurchaseOrderStatusSwitches'
import Image from '../../containers/Image'
import SupplierOrderReviewWrapper from './supplierOrderReviews.style'
import SuppliersOrdersFormContainer from '../../containers/Suppliers/Orders/SuppliersOrdersFormContainer'
import imagePreviewContainer from '../../containers/ImagePreview/ImagePreviewContainer'

import RangePickerWithDateType from '../../containers/RangePickerWithDateType'

const renderPOPreviewBtn = (supplierOrder) => {
    const isPODownloaded = supplierOrder.is_po_downloaded

    if (!isPODownloaded) { return null }

    return (
        <Button onClick={() => window.open(`/purchase_orders/${supplierOrder.ref_code}/previews`)}
            className="po-last-download-btn"
            ghost
            icon={<EyeOutlined />}
            type="primary" />
    )
}

const renderSupplierOrderBtn = (props, record) => {
    const supplierOrder = record.supplier_order

    if (!supplierOrder) { return null }

    const { loadPO, onShowSupplierOrderForm, onShowPOForm } = props
    let supplierName = null

    if (supplierOrder.state === 'get_order') {
        supplierName = (
            <a onClick={() => onShowSupplierOrderForm({ orderId: record.id, supplierOrderId: supplierOrder.id })}>
                <div>{supplierOrder.supplier_name}</div>
            </a>
        )
    } else {
        supplierName = <div>{supplierOrder.supplier_name}</div>
    }

    return (
        <div>
            {supplierName}
            <div>
                {loadPO ? <React.Fragment>
                    <Button className="icon-button" type="primary" icon={<FileTextOutlined />} ghost
                        onClick={() => onShowPOForm({
                            orderId: record.id,
                            supplierOrderId: supplierOrder.id,
                            refCode: supplierOrder.ref_code })} />
                    {renderPOPreviewBtn(supplierOrder)}
                </React.Fragment> : null}

            </div>
        </div>
    )
}

const renderPOForm = props => {
    const {
        i18n,
        activeOrder,
        activeSupplierOrder,
        onSaveSuccessPOForm,
        onShowPOForm,
        showPOFormModal
    } = props

    return (
        (<Modal
            width="70%"
            destroyOnClose
            footer={false}
            onCancel={() => onShowPOForm()}
            open={showPOFormModal}>
            <PurchaseOrderFormContainer
                onSuccess={() => onSaveSuccessPOForm()}
                orderId={activeOrder}
                supplierOrderId={activeSupplierOrder} />
        </Modal>)
    );
}

const renderSupplierOrderForm = props => {
    const {
        activeOrder,
        activeSupplierOrder,
        onChangePagination,
        onShowSupplierOrderForm,
        showSupplierOrderFormModal
    } = props

    return (
        (<Modal
            destroyOnClose
            footer={false}
            onCancel={() => onShowSupplierOrderForm()}
            open={showSupplierOrderFormModal}>
            <SuppliersOrdersFormContainer
                isNewPage={!activeSupplierOrder}
                isOrderSummaryDashBoard
                isSupplierPurchase
                formType="product"
                orderId={activeOrder}
                supplierOrderId={activeSupplierOrder}
                onSaveSuccess={() => {
                    onShowSupplierOrderForm()
                    onChangePagination(1)
                }}
            />
        </Modal>)
    );
}

const renderImage = (dataIndex) => {
    if (!dataIndex) {
        return
    }
    return dataIndex.map((image) => {
        const imageId = image.id
        const thumbUrl = image.file && image.file.s || ''
        const originalUrl = image.file && image.file.original || ''
        const contentType = image.file && image.file.content_type || ''

        return (
            <Image
                key={imageId}
                contentType={contentType}
                originalUrl={originalUrl}
                thumbUrl={thumbUrl}
            />
        )
    })
}

const renderCancelBtn = (props, supplierOrder) => {
    const { i18n, onCancelSupplierOrder } = props

    if (!supplierOrder) { return null }

    const state = supplierOrder.state

    if (state === 'get_order') {
        return (
            <Tooltip title={i18n.t('suppliers:cancel')}>
                <Button type="danger" icon={<CloseOutlined />}
                    onClick={() => {onCancelSupplierOrder(supplierOrder.id, supplierOrder.order)}} />
            </Tooltip>
        )
    } else if (state === 'cancel') {
        return <div>{supplierOrder.state_label}</div>
    }
}

const renderIsPoCompletedSelect = (props) => {
    const { i18n, defaultIsPoCompleted, handleFilterPoCreated } = props

    return (
        <Select
            allowClear
            value={defaultIsPoCompleted}
            onChange={(value) => handleFilterPoCreated(value)}
            style={{ width: '100%' }}
        >
            <Select.Option key="true" value="true">
                {i18n.t('suppliers:supplierOrderReviews:poCompleted')}
            </Select.Option>
            <Select.Option key="false" value="false">
                {i18n.t('suppliers:supplierOrderReviews:NotCreatedYet')}
            </Select.Option>
        </Select>
    )
}

const renderIsCheckedSelect = (props) => {
    const { i18n, defaultIsChecked, handleFilterIsChecked } = props

    return (
        <Select
            allowClear
            value={defaultIsChecked}
            onChange={(value) => handleFilterIsChecked(value)}
            style={{ width: '100%' }}
        >
            <Select.Option key="true" value="true">
                {i18n.t('suppliers:supplierOrderReviews:approved')}
            </Select.Option>
            <Select.Option key="false" value="false">
                {i18n.t('suppliers:supplierOrderReviews:NotApproved')}
            </Select.Option>
        </Select>
    )
}

const renderOrderState = (props, record) => {
    const { i18n } = props

    const paymentMethod = record.payment_method
    const isDeposit = record.is_deposit_payment_term
    const stateLabel = record.state_label

    if (record.state !== 'payment') { return stateLabel }

    const payByCheque = paymentMethod === 'cheque' ? `<${i18n.t('inquiries:payByCheque')}>` : ''
    const depositPaymentTerm = isDeposit ? `<${i18n.t('inquiries:stateDropdown.deposit')}>` : ''

    return (
        <div>
            <div>{stateLabel}</div>
            <div>{depositPaymentTerm}</div>
            <div>{payByCheque}</div>
        </div>
    )
}

const renderMobileCardItem = (props, record) => {
    const { i18n, onShowSupplierOrderForm } = props
    const supplierOrder = record.supplier_order
    const supplierOrderImages = _.get(supplierOrder, 'supplier_order_images', [])
    const orderSupplierNotes = _.get(supplierOrder, 'order_supplier_notes', [])
    const trucks = record.truck_dispatch_license_plates || []

    return (
        <Card size="small" className="supplier-review-card" style={{ marginBottom: 8 }}>
            <div className="card-row card-header">
                <Link to={`/inquiries/${record.number}`} className="order-number">
                    {record.number}
                </Link>
                <span className="card-state">{renderOrderState(props, record)}</span>
            </div>

            {record.detail ? (
                <div className="card-row subtle">{record.detail}</div>
            ) : null}

            <div className="card-row card-stats">
                <span>
                    <Typography.Text type="secondary">
                        {i18n.t('suppliers:supplierOrderReviews.paymentDateTime')}:{' '}
                    </Typography.Text>
                    {record.payment_date_time
                        ? dayjs(record.payment_date_time).format('DD MMM YYYY, h:mm:ss')
                        : '-'}
                </span>
                <span>
                    <Typography.Text type="secondary">
                        {i18n.t('suppliers:supplierOrderReviews.sumOfReceipt')}:{' '}
                    </Typography.Text>
                    <strong>{record.grand_total}</strong>
                    {' '}({record.sum_of_receipt === '0.00' ? '-' : record.sum_of_receipt})
                </span>
                {supplierOrder && supplierOrder.due_date ? (
                    <span>
                        <Typography.Text type="secondary">
                            {i18n.t('suppliers:supplierOrder.dueDate')}:{' '}
                        </Typography.Text>
                        {supplierOrder.due_date}
                    </span>
                ) : null}
            </div>

            <div className="card-row card-supplier">
                {renderSupplierOrderBtn(props, record)}
            </div>

            {supplierOrder ? (
                <div className="card-row">
                    <PurchaseOrderStatusSwitches supplierOrder={supplierOrder} />
                </div>
            ) : null}

            {trucks.length ? (
                <div className="card-row">
                    <Typography.Text type="secondary">
                        {i18n.t('suppliers:supplierOrderReviews.trucks')}:
                    </Typography.Text>
                    {trucks.map((truck, i) => (
                        <div key={`m-truck-${record.id}-${i}`} className="truck-dispatch">
                            {truck}
                        </div>
                    ))}
                </div>
            ) : null}

            {(supplierOrderImages.length || orderSupplierNotes.length) ? (
                <div className="card-row">
                    {supplierOrderImages.length ? (
                        <div className="attach-file">
                            <div className="image-container">
                                {renderImage(supplierOrderImages)}
                            </div>
                        </div>
                    ) : null}
                    {orderSupplierNotes.map((note) => (
                        <div key={`m-note-${note.id}`} className="attach-file">
                            <div>{note.body}</div>
                            <div className="image-container">{renderImage(note.images)}</div>
                        </div>
                    ))}
                </div>
            ) : null}

            {supplierOrder && supplierOrder.external_note ? (
                <div className="card-row external-note">
                    <strong>{i18n.t('suppliers:supplierOrder.externalNote')}:</strong>{' '}
                    {supplierOrder.external_note}
                </div>
            ) : null}

            {supplierOrder && supplierOrder.note_with_sanitizer ? (
                <div className="card-row supplier-order-note">
                    <strong>{i18n.t('suppliers:supplierOrder.supplierOrderNote')}:</strong>{' '}
                    {supplierOrder.note_with_sanitizer}
                </div>
            ) : null}

            <div className="card-row card-actions">
                <a onClick={() => onShowSupplierOrderForm({ orderId: record.id })}>
                    <PlusSquareTwoTone />
                </a>
                {renderCancelBtn(props, supplierOrder)}
            </div>
        </Card>
    )
}

const SupplierOrderReviewIndex = props => {
    const {
        dataSource,
        i18n,
        isMobile,
        loading,
        onChangePagination,
        onShowSupplierOrderForm,
        pagination,
        handleFilterOrderState,
        handleSupplierChange,
        renderPurchaserFilterOptions,
        handlePeriodChange,
        handleFilterPurchaser,
        handleSearch,
        handleFilterIsNotWorkingYet,
        handleDueDate,
        defaultSearchQuery,
        defaultOrderState,
        defaultSupplier,
        defaultPurchaser,
        defaultDateType,
        defaultStartDate,
        defaultEndDate,
        defaultIsNotWorking,
        defaultDueDate
    } = props

    const [filtersOpen, setFiltersOpen] = React.useState(false)

    const activeFilterCount = [
        defaultOrderState && defaultOrderState !== 'all',
        defaultSupplier,
        defaultPurchaser,
        defaultStartDate,
        defaultEndDate,
        defaultDueDate,
        props.defaultIsPoCompleted,
        props.defaultIsChecked,
        defaultIsNotWorking
    ].filter(Boolean).length

    const columns = [
        {
            key: 'order-number',
            title: i18n.t('suppliers:supplierOrderReviews.orderNumber'),
            width: 160,
            render: (record) => (
                <div>
                    <Link to={`/inquiries/${record.number}`}>
                        {record.number}
                    </Link>
                    <div>{renderOrderState(props, record)}</div>
                    {renderSupplierOrderBtn(props, record)}
                    <Divider type="horizontal" style={{ margin: '8px 0' }} />
                </div>
            )
        },
        {
            key: 'detail',
            title: i18n.t('suppliers:supplierOrderReviews.detail'),
            dataIndex: 'detail',
            width: 250
        },
        {
            key: 'payment_time',
            title: i18n.t('suppliers:supplierOrderReviews.paymentDateTime'),
            sorter: true,
            dataIndex: 'payment_date_time',
            render: (data) => {
                return (
                    <div>
                        {data ? dayjs(data).format('DD MMM YYYY, h:mm:ss') : null}
                    </div>
                )
            }
        },
        {
            key: 'grand_total_satangs',
            title: i18n.t('suppliers:supplierOrderReviews.sumOfReceipt'),
            sorter: true,
            render: (record) => (
                <div>
                    <div>{record.grand_total}</div>
                    <div>({record.sum_of_receipt === '0.00' ? '-' : record.sum_of_receipt})</div>
                </div>
            )
        },
        {
            key: 'attach-files',
            title: i18n.t('suppliers:attachFile'),
            width: 150,
            render: (record) => {
                const supplierOrder = record.supplier_order
                const addSupplierOrderBtn = (
                    <a onClick={() => onShowSupplierOrderForm({ orderId: record.id })}>
                        <PlusSquareTwoTone />
                    </a>
                )

                if (!supplierOrder) { return addSupplierOrderBtn }

                const supplierOrderImages = _.get(supplierOrder, 'supplier_order_images', [])
                return (
                    <div>
                        {supplierOrderImages.length ? (
                            <div key="supplier-order-images" className="attach-file">
                                <div className="image-container">
                                    {renderImage(supplierOrder.supplier_order_images)}
                                </div>
                            </div>
                        ) : null}
                        {_.get(supplierOrder, 'order_supplier_notes', []).map((orderSupplierNote) => {
                            return (
                                <div key={`order-supplier-note-${orderSupplierNote.id}`} className="attach-file">
                                    <div>{orderSupplierNote.body}</div>
                                    <div className="image-container">
                                        {renderImage(orderSupplierNote.images)}
                                    </div>
                                </div>
                            )
                        })}
                        {
                            supplierOrder.external_note ? <div className="external-note">
                                <span style={{ fontWeight: 'bold' }}>
                                    {i18n.t('suppliers:supplierOrder.externalNote')}:
                                </span> {supplierOrder.external_note}
                            </div> : null
                        }
                        {
                            supplierOrder.note_with_sanitizer ? <div className="supplier-order-note">
                                <span style={{ fontWeight: 'bold' }}>
                                    {i18n.t('suppliers:supplierOrder.supplierOrderNote')}:
                                </span> {supplierOrder.note_with_sanitizer}
                            </div> : null
                        }
                        {addSupplierOrderBtn}
                    </div>
                )
            }
        },
        {
            key: 'supplier-order-status',
            title: i18n.t('suppliers:state'),
            render: (record) => {
                const supplierOrder = record.supplier_order
                if (!supplierOrder) { return null }

                return <PurchaseOrderStatusSwitches supplierOrder={supplierOrder} />
            }
        },
        {
            key: 'truck_reservations.date',
            sorter: true,
            title: i18n.t('suppliers:supplierOrderReviews.trucks'),
            dataIndex: 'truck_dispatch_license_plates',
            render: (dataIndex, record, index) => {
                return dataIndex.map((truck, i) => {
                    return (
                        <div key={`truck-${index + 1}-${i + 1}`} className="truck-dispatch">{truck}</div>
                    )
                })
            }
        },
        {
            key: 'due-date',
            title: i18n.t('suppliers:supplierOrder.dueDate'),
            dataIndex: ['supplier_order', 'due_date']
        },
        {
            key: 'cancel',
            title: '',
            render: (record) => (
                <div className="cancel">
                    {renderCancelBtn(props, record.supplier_order)}
                </div>
            )
        }
    ]

    return (
        <SupplierOrderReviewWrapper>
            {/* Search Input */}
            <Input.Search
                enterButton
                defaultValue={defaultSearchQuery}
                onSearch={(query) => {handleSearch(query)}}
                placeholder={i18n.t('orders/tables:searchPlaceholder')} />

            {isMobile ? (
                <Button
                    block
                    className="filter-toggle"
                    icon={<FilterOutlined />}
                    onClick={() => setFiltersOpen((v) => !v)}
                >
                    {filtersOpen
                        ? i18n.t('common:hideFilters', { defaultValue: 'Hide filters' })
                        : i18n.t('common:showFilters', { defaultValue: 'Show filters' })}
                    {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                    {filtersOpen ? <UpOutlined /> : <DownOutlined />}
                </Button>
            ) : null}

            <div className={`filter-panel${isMobile && !filtersOpen ? ' filter-panel--hidden' : ''}`}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-field">
                            <label className="filter-label">
                                {i18n.t('orders:orderSummary.status')}
                            </label>
                            <SelectOrderStateContainer
                                onChange={handleFilterOrderState}
                                value={defaultOrderState || 'all'}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-field">
                            <label className="filter-label">
                                {i18n.t('suppliers:supplierName')}
                            </label>
                            <SelectSupplier
                                isShowHaveNoSupplier
                                onChange={handleSupplierChange}
                                value={defaultSupplier}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-field">
                            <label className="filter-label">
                                {i18n.t('suppliers:purchaseOrders.filterByPurchaser')}
                            </label>
                            <Select
                                allowClear
                                value={defaultPurchaser}
                                filterOption={(input, option) => {
                                    return option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }}
                                onChange={(value) => handleFilterPurchaser(value)}
                                placeholder={i18n.t('suppliers:purchaseOrders.filterByPurchaser')}
                                showSearch
                                style={{ width: '100%' }}
                            >
                                {renderPurchaserFilterOptions()}
                            </Select>
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={6}>
                        <div className="filter-field">
                            <label className="filter-label">
                                {i18n.t('suppliers:supplierOrderReviews.paymentDateTime')}
                            </label>
                            <RangePickerWithDateType
                                dateTypes={[
                                    {
                                        label: i18n.t('suppliers:supplierOrderReviews.truckReservationDate'),
                                        value: 'reservation_date'
                                    },
                                    {
                                        label: i18n.t('suppliers:supplierOrderReviews.paymentDateTime'),
                                        value: 'payment_time'
                                    }
                                ]}
                                onChange={handlePeriodChange}
                                value={{ date_type: defaultDateType, start_date: defaultStartDate, end_date: defaultEndDate }}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-field">
                            <label className="filter-label">
                                {i18n.t('suppliers:supplierOrder.dueDate')}
                            </label>
                            <DatePicker
                                onChange={handleDueDate}
                                defaultValue={defaultDueDate ? dayjs(defaultDueDate) : null}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-field">
                            <label className="filter-label">
                                {i18n.t('suppliers:supplierOrder.isPoCompleted')}
                            </label>
                            {renderIsPoCompletedSelect(props)}
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-field">
                            <label className="filter-label">
                                {i18n.t('suppliers:supplierOrder.isChecked')}
                            </label>
                            {renderIsCheckedSelect(props)}
                        </div>
                    </Col>

                    <Col xs={24} sm={12} md={12} lg={6}>
                        <div className="filter-field filter-field--checkbox">
                            <Checkbox onChange={handleFilterIsNotWorkingYet} checked={defaultIsNotWorking}>
                                {i18n.t('suppliers:supplierOrderReviews.NotWorkingYet')}
                            </Checkbox>
                        </div>
                    </Col>
                </Row>
            </div>
            <Divider />
            {isMobile ? (
                <List
                    className="supplier-review-mobile-list"
                    loading={loading}
                    dataSource={dataSource}
                    rowKey={record => record.supplier_order && record.supplier_order.id || record.id}
                    pagination={{
                        ...pagination,
                        onChange: (page) => onChangePagination({ current: page }, {}, {})
                    }}
                    renderItem={(record) => (
                        <List.Item style={{ padding: 0, border: 'none' }}>
                            {renderMobileCardItem(props, record)}
                        </List.Item>
                    )}
                />
            ) : (
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    onChange={onChangePagination}
                    pagination={pagination}
                    rowKey={record => record.supplier_order && record.supplier_order.id || record.id}
                    scroll={isMobileDevice ? { x: 1300 } : { x: 1000 }} />
            )}
            {renderSupplierOrderForm(props)}
            {renderPOForm(props)}
        </SupplierOrderReviewWrapper>
    )
}

SupplierOrderReviewIndex.propTypes = {
    activeOrder: PropTypes.string,
    activeSupplierOrder: PropTypes.string,
    dataSource: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    loadPO: PropTypes.bool,
    onCancelSupplierOrder: PropTypes.func.isRequired,
    onChangePagination: PropTypes.func.isRequired,
    onSaveSuccessPOForm: PropTypes.func.isRequired,
    onShowPOForm: PropTypes.func.isRequired,
    onShowSupplierOrderForm: PropTypes.func.isRequired,
    pagination: PropTypes.object.isRequired,
    showPOFormModal: PropTypes.bool,
    showSupplierOrderFormModal: PropTypes.bool
}

const TranslatedSupplierOrderReviewIndex = withTranslation(['common', 'suppliers', 'inquiries'])(
    imagePreviewContainer(SupplierOrderReviewIndex)
)

const SupplierOrderReviewIndexWithBreakpoint = (props) => {
    const screens = Grid.useBreakpoint()
    const isMobile = !screens.md
    return <TranslatedSupplierOrderReviewIndex {...props} isMobile={isMobile} />
}

export default SupplierOrderReviewIndexWithBreakpoint
